import { useState, useCallback } from "react";
import Editor from "@monaco-editor/react";
import { chat } from "../lib/api";

interface FileEditorProps {
  /** Initial file content */
  content: string;
  /** File path (e.g. "src/index.ts") */
  path: string;
  /** Repo name (e.g. "AI-APP") */
  repo: string;
  /** Branch */
  branch?: string;
  /** Language for syntax highlighting */
  language?: string;
  /** Called when file is saved */
  onSave?: (result: any) => void;
  /** Called to close the editor */
  onClose?: () => void;
  className?: string;
}

function detectLanguage(path: string): string {
  const ext = path.split(".").pop()?.toLowerCase() || "";
  const map: Record<string, string> = {
    ts: "typescript", tsx: "typescript", js: "javascript", jsx: "javascript",
    py: "python", rs: "rust", go: "go", rb: "ruby",
    json: "json", toml: "toml", yaml: "yaml", yml: "yaml",
    sql: "sql", md: "markdown", html: "html", css: "css",
    sh: "shell", bash: "shell", zsh: "shell",
    dockerfile: "dockerfile", xml: "xml", graphql: "graphql",
  };
  return map[ext] || "plaintext";
}

export function FileEditor({ content, path, repo, branch = "main", language, onSave, onClose, className }: FileEditorProps) {
  const [value, setValue] = useState(content);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [commitMsg, setCommitMsg] = useState(`Update ${path}`);
  const [targetBranch, setTargetBranch] = useState("");
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  const hasChanges = value !== content;
  const lang = language || detectLanguage(path);

  const handleSave = useCallback(async () => {
    if (!hasChanges) return;
    if (!targetBranch.trim()) { setError("Branch name required (can't commit to main)"); return; }

    setSaving(true);
    setError(null);
    try {
      // Use the chat API to call github_commit_file tool
      const res = await chat({
        messages: [{ role: "user", content: `Commit the following file change. Do not add commentary, just call the tool.\n\nRepo: ${repo}\nPath: ${path}\nBranch: ${targetBranch}\nMessage: ${commitMsg}\n\nFile content:\n\`\`\`\n${value}\n\`\`\`` }],
        route_mode: "chat.fast",
        system_prompt: "You are a file commit assistant. Call github_commit_file with the exact content provided. Do not modify the content.",
        approved_tool_ids: ["*"], // auto-approve
      });

      setSaved(true);
      setShowSaveDialog(false);
      setTimeout(() => setSaved(false), 3000);
      onSave?.(res);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [value, targetBranch, commitMsg, repo, path, hasChanges]);

  return (
    <div className={`flex flex-col h-full bg-background ${className || ""}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-card">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs text-muted-foreground">{repo}</span>
          <span className="text-xs text-muted-foreground">/</span>
          <span className="text-xs text-foreground font-mono truncate">{path}</span>
          <span className="text-[10px] text-muted-foreground bg-background px-1.5 py-0.5 rounded">{lang}</span>
          {hasChanges && <span className="w-2 h-2 rounded-full bg-warning" title="Unsaved changes" />}
          {saved && <span className="text-[10px] text-success">✓ Saved</span>}
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <button
              onClick={() => setShowSaveDialog(true)}
              className="px-3 py-1 text-xs font-medium bg-success text-success-foreground rounded hover:bg-success/90"
            >
              Save to Branch
            </button>
          )}
          {onClose && (
            <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground">
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Save dialog */}
      {showSaveDialog && (
        <div className="px-3 py-2 border-b border-border bg-card space-y-2">
          <div className="flex gap-2">
            <input
              value={targetBranch}
              onChange={(e) => setTargetBranch(e.target.value)}
              placeholder="Branch name (e.g. fix/update-file)"
              className="flex-1 bg-background border border-border rounded px-2 py-1 text-xs text-foreground font-mono"
            />
            <input
              value={commitMsg}
              onChange={(e) => setCommitMsg(e.target.value)}
              placeholder="Commit message"
              className="flex-1 bg-background border border-border rounded px-2 py-1 text-xs text-foreground"
            />
          </div>
          {error && <div className="text-xs text-destructive">{error}</div>}
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving} className="px-3 py-1 text-xs bg-success text-success-foreground rounded hover:bg-success/90 disabled:opacity-50">
              {saving ? "Committing..." : "Commit"}
            </button>
            <button onClick={() => setShowSaveDialog(false)} className="px-3 py-1 text-xs text-muted-foreground hover:text-foreground">Cancel</button>
          </div>
        </div>
      )}

      {/* Editor */}
      <div className="flex-1">
        <Editor
          height="100%"
          language={lang}
          value={value}
          onChange={(v) => setValue(v || "")}
          theme="vs-dark"
          options={{
            fontSize: 13,
            fontFamily: "'JetBrains Mono', 'Fira Code', 'SF Mono', Menlo, monospace",
            minimap: { enabled: false },
            lineNumbers: "on",
            scrollBeyondLastLine: false,
            wordWrap: "on",
            tabSize: 2,
            renderWhitespace: "selection",
            bracketPairColorization: { enabled: true },
            padding: { top: 8 },
          }}
        />
      </div>
    </div>
  );
}
